// <s-product-card> keeps a product card's size/colour choice, price and add-to-cart in sync.
// It reads variant data from [data-card-variants], writes the chosen id into input[name="id"],
// and adds to cart through Shopify Standard Actions so the header cart count refreshes in place.
class SProductCard extends HTMLElement {
  #ac;
  #variants = null;
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLElement && event.target.matches('[data-option-index]')) this.#sync();
    }, opts);

    this.addEventListener('submit', (event) => this.#onSubmit(event), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #getVariants() {
    if (this.#variants) return this.#variants;
    try {
      this.#variants = JSON.parse(this.querySelector('[data-card-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }
    return this.#variants;
  }

  #selectedValues() {
    const values = [];
    for (const control of this.querySelectorAll('[data-option-index]')) {
      if (control instanceof HTMLInputElement && control.type === 'radio' && !control.checked) continue;
      values[Number(control.dataset.optionIndex)] = control.value;
    }
    return values;
  }

  #sync() {
    const values = this.#selectedValues();
    const variant = this.#getVariants().find((candidate) =>
      candidate.options.every((value, index) => values[index] === undefined || values[index] === value)
    );

    const idInput = this.querySelector('input[name="id"]');
    const button = this.querySelector('[data-card-add]');
    const price = this.querySelector('[data-card-price]');
    const compare = this.querySelector('[data-card-compare]');
    const discount = this.querySelector('[data-card-discount]');

    for (const label of this.querySelectorAll('[data-selected-label]')) {
      const index = Number(label.dataset.selectedLabel);
      if (values[index] !== undefined) label.textContent = values[index];
    }

    if (variant && idInput) idInput.value = variant.id;
    if (variant && price) price.textContent = variant.price;

    if (compare) {
      compare.textContent = variant?.compare || '';
      compare.hidden = !variant?.compare;
    }
    if (discount) {
      discount.textContent = variant?.discount || '';
      discount.hidden = !variant?.discount;
    }

    if (button instanceof HTMLElement) {
      const available = Boolean(variant?.available);
      button.setAttribute('aria-disabled', available ? 'false' : 'true');
      button.classList.toggle('opacity-50', !available);
      button.textContent = available ? this.dataset.addLabel : this.dataset.soldOutLabel;
    }
  }

  async #onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    // Without Standard Actions the native product form posts to the cart as a fallback.
    if (!window.Shopify?.actions?.updateCart) return;
    event.preventDefault();

    const button = form.querySelector('[data-card-add]');
    if (this.#busy || button?.getAttribute('aria-disabled') === 'true') return;

    const idInput = form.elements.namedItem('id');
    const variantId = idInput instanceof HTMLInputElement ? idInput.value.trim() : '';
    if (!variantId) return;

    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    button?.classList.add('cursor-wait', 'opacity-50');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: variantId, quantity: 1 }] },
        { event: { context: 'product', detail: { source: 's-product-card', productId: this.dataset.productId } } }
      );
      // Cart errors are already surfaced as toasts by the Standard Actions override.
      if (!result?.userErrors?.length) this.#toast(this.dataset.addedMessage, 'success');
    } catch {
      // The Standard Actions override reports failures; nothing extra to show here.
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
      button?.classList.remove('cursor-wait');
      if (button?.getAttribute('aria-disabled') !== 'true') button?.classList.remove('opacity-50');
    }
  }

  #toast(message, variant) {
    if (!message) return;
    document.dispatchEvent(new CustomEvent('s-notification:add', { detail: { message, variant, duration: 3000 } }));
  }
}

if (!customElements.get('s-product-card')) {
  customElements.define('s-product-card', SProductCard);
}
